import { Layouts } from '../../../controls/layouts';
import { Buttons } from '../../../controls/buttons';
import { Component, computed, forwardRef, inject, model, resource, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, Validator } from '@angular/forms';
import { Selectbox } from '../../../controls/selectbox';
import { TransportProtocol } from 'src/app/partnerportal/components/select-transport-dialog/select-transport-dialog.component';
import { Transport, TransportType } from 'src/app/partnerportal/models/partner-onboarding';
import { LinkComponent } from 'src/app/controls/components/basic/link/link.component';
import { SftpProtocolViewerComponent } from './components/protocols/SFTP/sftp-protocol-viewer/sftp-protocol-viewer.component';
import { As2ProtocolViewerComponent } from './components/protocols/AS2/as2-protocol-viewer/as2-protocol-viewer.component';
import { PartnerOnboardingTransportService } from './services/transport-service';
import { FtpProtocolViewerComponent } from './components/protocols/FTP/ftp-protocol-viewer/ftp-protocol-viewer.component';
import { SectionBoxComponent } from './components/section-box/section-box.component';

@Component({
  selector: 'app-partner-onboarding-transport',
  templateUrl: './partner-onboarding-transport.component.html',
  styleUrl: './partner-onboarding-transport.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PartnerOnboardingTransportComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PartnerOnboardingTransportComponent),
      multi: true
    }
  ],
  imports: [FormsModule, Layouts, Selectbox, LinkComponent, Buttons, SftpProtocolViewerComponent, As2ProtocolViewerComponent, FtpProtocolViewerComponent, SectionBoxComponent]
})
export class PartnerOnboardingTransportComponent implements Validator, ControlValueAccessor {
  transportService = inject(PartnerOnboardingTransportService);
  form = viewChild<NgForm>('form');
  value = model<Transport[]>([]);

  private formChangedTimer: ReturnType<typeof setTimeout> | null = null;

  showAddTransportProtocol = false;
  addedTransportProtocols = signal<TransportProtocol[]>([]);
  initialTransportProtocols = signal<TransportProtocol[]>([]);
  selectedTransportProtocol = signal<TransportProtocol>(null);
  disabled = signal(false);
  readonly allTransportProtocols: TransportProtocol[] = Object.values(TransportProtocol)
    .filter((v): v is TransportProtocol => typeof v === 'string');

  /*
   * Computed properties
   */

  // Protocols that are either already used (initial) or have been added
  usedTransportProtocols = computed<TransportProtocol[]>(() => {
    const initial = this.initialTransportProtocols() ?? [];
    const added = this.addedTransportProtocols();
    return Array.from(new Set(initial.concat(added)));
  });

  // Protocols that can still be added (not already used or added)
  transportProtocolsToSelectFrom = computed<TransportProtocol[]>(() => this.allTransportProtocols.filter(protocol => !this.usedTransportProtocols().includes(protocol)));

  // Transports for each protocol, used to bind to the protocol viewers
  sftpTransports = computed<Transport[]>(() => this.value()?.filter(t => t.Type === TransportType.SFTPCustomerEndpoint || t.Type === TransportType.SFTPPartnerEndpoint || t.Type === TransportType.IntegratedSFTP) ?? []);
  ftpTransports = computed<Transport[]>(() => this.value()?.filter(t => t.Type === TransportType.FTPCustomerEndpoint || t.Type === TransportType.FTPPartnerEndpoint || t.Type === TransportType.IntegratedFTP) ?? []);

  // AS2 doesn't have multiple transports, so we just take the first one we find for the AS2 protocol viewer.
  // If none is found, we generate a new one.
  transportResource = resource({
      params: () => ({
          existingTransport: this.value()?.find(t => t.Type === TransportType.AS2),
      }),
      loader: ({ params }) => {
        if (params.existingTransport) {
          return Promise.resolve(params.existingTransport);
        }
        return this.transportService.generateTransport(TransportType.AS2);
      }
  });
  as2Transport = computed(() => this.transportResource.value());


  /*
   * Event handlers
   */

  transportProtocolChanged(protocol: TransportProtocol): void {
    this.selectedTransportProtocol.set(protocol);
  }

  addMoreTransportProtocolsClicked(): void {
    this.showAddTransportProtocol = true;
  }

  addSelectedTransportProtocolClicked(): void {
    const protocol = this.selectedTransportProtocol();
    if (!protocol) return;
    this.addedTransportProtocols.update(protocols => [...protocols, protocol]);
    this.selectedTransportProtocol.set(null);
    this.showAddTransportProtocol = false;
    this.formChangedAfterViewUpdate();
  }

  removeTransportProtocol(protocol: TransportProtocol): void {
    // If it's an added protocol, just remove it from the added list
    if (this.addedTransportProtocols().includes(protocol)) {
      this.addedTransportProtocols.update(protocols => protocols.filter(p => p !== protocol));
      this.formChangedAfterViewUpdate();
      return;
    }
    this.initialTransportProtocols.update(protocols => protocols.filter(p => p !== protocol));
    this.formChangedAfterViewUpdate();
  }

  private formChangedAfterViewUpdate(): void {
    if (this.formChangedTimer) {
      return;
    }
    this.formChangedTimer = setTimeout(() => {
      this.formChangedTimer = null;
      this.formChanged();
    }, 0);
  }

  formChanged() {
    const formTransports = this.usedTransportProtocols().map(protocol => {
      if (protocol === TransportProtocol.AS2) {
        const field = this.form()?.form.get('protocol-' + protocol)
        if (field) {
          const value = field.getRawValue() as Transport;
          return value ? [value] : [];
        }
        return [];
      }
      else {
        const field = this.form()?.form.get('protocol-' + protocol)
        if (field) {
          return field.getRawValue() as Transport[];
        }
        return [];
      }
    }).flat();
    this.value.set(formTransports);
    this.onChange(formTransports);
  }

  getUsedTransportProtocols(transports: Transport[]): TransportProtocol[] {
    const usedProtocols = new Set<TransportProtocol>();
    for (const transport of transports ?? []) {
      switch (transport.Type) {
        case TransportType.SFTPCustomerEndpoint:
        case TransportType.SFTPPartnerEndpoint:
        case TransportType.IntegratedSFTP:
          usedProtocols.add(TransportProtocol.SFTP);
          break;
        case TransportType.FTPCustomerEndpoint:
        case TransportType.FTPPartnerEndpoint:
        case TransportType.IntegratedFTP:
          usedProtocols.add(TransportProtocol.FTP);
          break;
        case TransportType.AS2:
          usedProtocols.add(TransportProtocol.AS2);
          break;
      }
    }
    return Array.from(usedProtocols) ?? [];
  }


  /*
   * Validator interface methods
   */

  validate() {
    const frm = this.form()?.form;
    if (frm?.disabled ?? true) {
      return [];
    }
    return frm?.valid ? [] : [{ valid: false, message: 'Invalid in transport component' }];
  }


  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: Transport[]): void {
    this.value.set(obj);
    this.initialTransportProtocols.set(this.getUsedTransportProtocols(this.value()));
    this.addedTransportProtocols.set([]);
  }

  onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
    this.showAddTransportProtocol = false;
  }
}
